import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { getKioskAuthorization, kioskPaymentStop } from 'api/Kiosk';

import { socket, socketEmit, socketOff, socketOn } from 'lib/clients/socket';
import useError from 'lib/hooks/useError';

import ErrorPaymentModal from 'components/Payments/NewPayment/PaymentContent/components/ErrorModal';
import { usePaymentContext } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';
import Modal from 'components/shared/Modal/Modal';

import CardContent from './components/CardContent';
import Footer from './components/Footer';
import Header from './components/Header';
import Information from './components/Information';

const PayFromKioskModal = ({ isOpen, handleClose, patientId, onSuccess, provider }) => {
  const [active, setActive] = useState('saved-cards');

  const { values } = usePaymentContext();
  const scrollRef = useRef(null);
  const { error, setErrorMessage } = useError();
  const navigate = useNavigate();

  const timeoutRef = useRef(null);

  const { data } = useQuery({
    queryKey: ['kiosk-authorization', patientId],
    queryFn: () => getKioskAuthorization(navigate, { patientId }),
    enabled: isOpen && !!patientId,
    refetchOnMount: 'always',
    staleTime: 0
  });

  const clearTimeoutHandler = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const closeModal = () => {
    handleClose();
    setActive('saved-cards');
    setErrorMessage(null);
    clearTimeoutHandler();
  };

  const goBack = () => {
    if (active === 'credit-card') {
      setActive('saved-cards');
      return;
    }

    closeModal();
  };

  const handlePaymentSuccess = () => {
    clearTimeoutHandler();
  };

  const initSocket = (kAuth) => {
    try {
      if (!kAuth || kAuth === 'not_configured') {
        return;
      }

      socketOn('kioskPaymentScreenMessage', (data) => {
        if (data?.transactionId) {
          onSuccess(data?.transactionId);
        }
        switch (data?.kind) {
          case 'handlePaymentConfirmationReceived':
            handlePaymentSuccess();
            break;
          case 'switchToAlternative':
            setActive('credit-card');
            break;
          case 'success':
            handlePaymentSuccess();
            onSuccess(data?.transactionId);
            break;
          case 'error':
            setErrorMessage(data?.message);
            break;
        }
      });

      socketEmit('kioskJoinClient', { kioskAuthorization: kAuth }, { reemitOnReconnect: true });

      timeoutRef.current = setTimeout(async () => {
        const data = await kioskPaymentStop(navigate, {});
        if (data?.transaction?.status) {
          if (data?.transaction?.status === 'completed') {
            handlePaymentSuccess();
            onSuccess(data?.transaction?.id);
          } else {
            setErrorMessage(data?.message);
          }
        } else {
          setErrorMessage('Terminal not connected, please try again.');
        }
      }, 60000);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    initSocket(data?.kioskAuthorization);
    return () => {
      socketOff('kioskPaymentScreenMessage');
      clearTimeoutHandler();
    };
  }, [data?.kioskAuthorization]);

  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }
  }, [socket.connected]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        handleClose={closeModal}
        width="full"
        title={<Header goBack={goBack} step={active} />}
        footer={<Footer step={active} amount={values.amount} onClose={goBack} />}>
        <div className="flex h-full flex-col items-center justify-center">
          <div
            className="flex w-full max-w-[448px] flex-col items-center gap-6 px-4 pb-6 pt-4"
            ref={scrollRef}>
            <Information active={active} />
            <CardContent
              ref={scrollRef}
              active={active}
              setActive={setActive}
              setErrorMessage={setErrorMessage}
              patientId={patientId}
              provider={provider}
            />
          </div>
        </div>
      </Modal>
      {error && (
        <ErrorPaymentModal
          message={error}
          onClearError={() => setErrorMessage(null)}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default PayFromKioskModal;
